import moment from 'moment';

import type {
    AddButtonPropsMultiple,
    AddButtonPropsSingular,
    EmptyStateProps,
} from 'shells/card/card-widget/card-widget';
import {TaskTickOutlineIcon} from 'shells/icon/svg/task-tick-outline';
import type {SelectMenuOption} from 'shells/select-menu/select-menu';

import type {CoreEntityTaskFragment} from 'nutshell-graphql-types';

import {ReminderIcon, REMINDER_OPTIONS} from './consts';
import type {TaskTab} from './tasks-widget';

export function getDefaultTaskDueTime(defaultDueTime?: number | null): number {
    return defaultDueTime || moment().add(1, 'day').unix();
}

export function getEmptyStateText(selectedTab: TaskTab): string {
    if (selectedTab === 'open') {
        return 'No open tasks';
    } else if (selectedTab === 'completed') {
        return 'No completed tasks';
    }

    return 'No tasks';
}

export function getEmptyStateProps(
    tasks: CoreEntityTaskFragment[] | null | undefined,
    selectedTab: TaskTab,
    isAddingTask: boolean
): EmptyStateProps {
    const text = getEmptyStateText(selectedTab);
    const isEmpty = tasks ? tasks.length === 0 && !isAddingTask : true;

    return {
        text,
        isEmpty,
        icon: TaskTickOutlineIcon,
        iconSize: 22,
    };
}

export function getTabOptions(
    tasks: CoreEntityTaskFragment[] | null | undefined,
    selectedTab: TaskTab,
    handleTabChange: (tab: TaskTab) => void
) {
    const openTasks = tasks ? tasks.filter((task) => task.isCompleted === false) : [];
    const completedTasks = tasks ? tasks.filter((task) => task.isCompleted === true) : [];

    return {
        // These match the TaskTab type in tasks-widget.jsx
        options: [
            {
                id: 'open',
                label: 'Open',
                count: openTasks.length ? String(openTasks.length) : '0',
            },
            {
                id: 'completed',
                label: 'Completed',
                count: completedTasks.length ? String(completedTasks.length) : '0',
            },
        ],
        selectedTab,
        onChangeTab: (tab: string) => {
            // @ts-expect-error
            handleTabChange(tab);
        },
    };
}

export function insertTasksAtStoredPositions(
    baseTasks: CoreEntityTaskFragment[],
    tasks: CoreEntityTaskFragment[],
    tasksToKeepInCurrentTab: Map<string, number>
): CoreEntityTaskFragment[] {
    if (!tasksToKeepInCurrentTab || tasksToKeepInCurrentTab.size === 0) {
        return baseTasks;
    }

    const tasksToKeep = tasks.filter((task) => tasksToKeepInCurrentTab.has(task.id));

    const tasksToKeepWithPositions = [
        ...tasksToKeep.map((task) => ({
            task,
            position: tasksToKeepInCurrentTab.get(task.id) || 0,
        })),
    ];

    tasksToKeepWithPositions.sort((a, b) => a.position - b.position);

    // Filter out tasks from baseTasks that are being kept at stored positions
    // to prevent duplicates
    const filteredBaseTasks = baseTasks.filter((task) => !tasksToKeepInCurrentTab.has(task.id));

    const result = [];
    let baseIndex = 0;
    let insertIndex = 0;

    for (let i = 0; i < filteredBaseTasks.length + tasksToKeepWithPositions.length; i++) {
        const nextInsert = tasksToKeepWithPositions[insertIndex];

        if (nextInsert && nextInsert.position === i) {
            result.push(nextInsert.task);
            insertIndex++;
        } else if (baseIndex < filteredBaseTasks.length) {
            result.push(filteredBaseTasks[baseIndex]);
            baseIndex++;
        }
    }

    // @ts-expect-error
    return result;
}

export function getTasksList(
    tasks: CoreEntityTaskFragment[] | null | undefined,
    selectedTab: TaskTab,
    tasksToKeepInCurrentTab?: Map<string, number>
): CoreEntityTaskFragment[] {
    if (!tasks) {
        return [];
    }

    let baseTasks: CoreEntityTaskFragment[] = [];

    switch (selectedTab) {
        case 'open':
            baseTasks = [...tasks.filter((task) => task.isCompleted === false)];

            break;
        case 'completed':
            baseTasks = [...tasks.filter((task) => task.isCompleted === true)];
            break;
        default:
            return [];
    }

    if (tasksToKeepInCurrentTab) {
        baseTasks = insertTasksAtStoredPositions(baseTasks, tasks, tasksToKeepInCurrentTab);
    }

    return baseTasks;
}

export function adjustPositionsAfterDelete(
    tasksToKeepInCurrentTab: Map<string, number>,
    deletedTaskIndex: number
): Map<string, number> {
    const updatedMap = new Map();

    tasksToKeepInCurrentTab.forEach((position, taskId) => {
        if (position > deletedTaskIndex) {
            updatedMap.set(taskId, position - 1);
        } else {
            updatedMap.set(taskId, position);
        }
    });

    return updatedMap;
}

export function getReminderMenuOptions(
    recurringTask: CoreEntityTaskFragment | null,
    onSetReminder: (days: number, title: string) => void,
    onRemoveReminder: () => void
): SelectMenuOption[] {
    const periodOptions = REMINDER_OPTIONS.filter(
        (opt) => !recurringTask || opt.days !== recurringTask.recurringPeriod
    ).map((opt) => ({
        label: opt.label,
        handleSelect: () => onSetReminder(opt.days, opt.title),
        icon: ReminderIcon,
    }));

    if (recurringTask) {
        return [
            ...periodOptions,
            {
                label: 'Stop reminders',
                handleSelect: onRemoveReminder,
                icon: ReminderIcon,
            },
        ];
    }

    return periodOptions;
}

export function getAddTaskButtonProps(
    onClick: () => void,
    isDisabled: boolean
): AddButtonPropsSingular {
    return {
        variant: 'singular',
        label: '+ Add task',
        onClick,
        isDisabled,
    };
}

export function getReminderButtonProps(
    selectMenuOptions: AddButtonPropsMultiple['selectMenuOptions'],
    isDisabled: boolean,
    onClick: () => void
): AddButtonPropsMultiple {
    return {
        variant: 'multiple',
        label: '+ Reminder',
        selectMenuOptions,
        isDisabled,
        onClick,
    };
}
